import { useInfiniteQuery } from "@tanstack/react-query";

import type { SkillCollectionVersionRecord } from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { PaginatedResponse } from "@/queries/types";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

type ListSkillCollectionVersionsResponse = PaginatedResponse<
  SkillCollectionVersionRecord[],
  "versions",
  string
>;

export const useListSkillCollectionVersions = (
  workspaceId: number | null,
  collectionId: string | null,
) => {
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  const perPage = 30;

  return useInfiniteQuery({
    queryKey: ["skill-collection-versions", workspaceId, collectionId, perPage],
    queryFn: async ({ pageParam = 1 }): Promise<ListSkillCollectionVersionsResponse> => {
      if (!workspaceId || !collectionId) {
        return {
          success: true,
          versions: [],
          has_prev: false,
          has_next: false,
          prev_num: null,
          next_num: null,
          page: 1,
          pages: 0,
          per_page: perPage,
          total: 0,
        };
      }
      const url = new URL(ENDPOINTS.skill_collections.versions(workspaceId, collectionId));
      url.searchParams.set("page", pageParam.toString());
      url.searchParams.set("per_page", perPage.toString());

      const res = await fetch(url.toString(), {
        headers: jsonAuthHeaders(userToken),
      });
      return handleResponse(res);
    },
    getNextPageParam: lastPage => (lastPage.has_next ? lastPage.next_num : undefined),
    enabled: !!workspaceId && !!collectionId && !!userToken,
  });
};
